import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { toIsoDateTimeString } from 'common/utils/date.utils';
import { toSafeLimit, toSafePage } from 'common/utils/math.utils';

export interface IObservationTimelineEntity {
  type: Components.Schemas.ObservationTimelineEntryType;
  id: number;
}

export interface IFetchObservationTimelineEntriesParams {
  schoolClassId: number;
  currentPage: number;
  pageSize: number;
  entryType?: Components.Schemas.ObservationTimelineEntryType | null;
  schoolSubjectId?: number | null;
  searchText?: string;
  performanceAssessmentId?: number | null;
  filterIncomplete?: boolean;
  filterActiveSupportMeasures?: boolean;
  includeSupportMeasures?: boolean;
  studentId?: number | null;
  competenceId?: number | null;
  competenceDomainId?: number | null;
  fromDate?: string;
  toDate?: string;
}

export type IFetchObservationTimelineStatsParams = Pick<
  IFetchObservationTimelineEntriesParams,
  | 'schoolClassId'
  | 'schoolSubjectId'
  | 'searchText'
  | 'studentId'
  | 'fromDate'
  | 'toDate'
  | 'competenceId'
  | 'competenceDomainId'
  | 'includeSupportMeasures'
  | 'entryType'
  | 'performanceAssessmentId'
  | 'filterIncomplete'
  | 'filterActiveSupportMeasures'
>;

export async function fetchObservationTimelineEntries(
  params: IFetchObservationTimelineEntriesParams,
): Promise<Components.Schemas.PaginatedObservationTimelineSummaryResponseDto> {
  const limit = toSafeLimit(params.pageSize);
  const offset = (toSafePage(params.currentPage) - 1) * limit;

  return Api.Routes.ObservationTimeline.retrieve({
    schoolClassId: String(params.schoolClassId),
    entryType: params.entryType ?? undefined,
    schoolSubjectId: params.schoolSubjectId != null ? String(params.schoolSubjectId) : undefined,
    studentId: params.studentId != null ? String(params.studentId) : undefined,
    competenceId: params.competenceId != null ? String(params.competenceId) : undefined,
    competenceDomainId: params.competenceDomainId != null ? String(params.competenceDomainId) : undefined,
    performanceAssessmentId:
      params.performanceAssessmentId != null ? String(params.performanceAssessmentId) : undefined,
    filterString: params.searchText || undefined,
    filterIncomplete: params.filterIncomplete ? 'true' : undefined,
    filterActiveSupportMeasures: params.filterActiveSupportMeasures ? 'true' : undefined,
    includeSupportMeasures: params.includeSupportMeasures ? 'true' : undefined,
    fromDate: params.fromDate ? toIsoDateTimeString(params.fromDate) : undefined,
    toDate: params.toDate ? toIsoDateTimeString(params.toDate) : undefined,
    offset: String(offset),
    limit: String(limit),
  });
}

export async function fetchObservationTimelineStats(
  params: IFetchObservationTimelineStatsParams,
): Promise<Components.Schemas.ObservationTimelineStatsResponseDto> {
  return Api.Routes.ObservationTimeline.retrieveStats({
    entryType: params.entryType ?? undefined,
    performanceAssessmentId:
      params.performanceAssessmentId != null ? String(params.performanceAssessmentId) : undefined,
    filterIncomplete: params.filterIncomplete ? 'true' : undefined,
    filterActiveSupportMeasures: params.filterActiveSupportMeasures ? 'true' : undefined,
    schoolClassId: String(params.schoolClassId),
    schoolSubjectId: params.schoolSubjectId != null ? String(params.schoolSubjectId) : undefined,
    studentId: params.studentId != null ? String(params.studentId) : undefined,
    competenceId: params.competenceId != null ? String(params.competenceId) : undefined,
    competenceDomainId: params.competenceDomainId != null ? String(params.competenceDomainId) : undefined,
    filterString: params.searchText || undefined,
    includeSupportMeasures: params.includeSupportMeasures ? 'true' : undefined,
    fromDate: params.fromDate ? toIsoDateTimeString(params.fromDate) : undefined,
    toDate: params.toDate ? toIsoDateTimeString(params.toDate) : undefined,
  });
}

/** Uses the same access checks and summary projection as the paginated feed. */
export async function fetchObservationTimelineEntryById(
  schoolClassId: number,
  studentId: number | null,
  entity: IObservationTimelineEntity,
): Promise<Components.Schemas.ObservationTimelineSummaryDto> {
  const response = await Api.Routes.ObservationTimeline.retrieveById({
    entryType: entity.type,
    id: entity.id,
    schoolClassId: String(schoolClassId),
    studentId: studentId != null ? String(studentId) : undefined,
  });
  return response.data;
}
